// Croatian + English string tables for the members app. The active language lives in the Vuex store
// (`state.language`), so `$t(key, params)` and `$strings` reactively follow the switcher. Use `applyLanguage`
// to change it everywhere at once (store + moment locale).
import moment from 'moment';
import store from './store.js';

const strings = {
    hr: {
        appName: 'Fytlix',

        // Login / auth
        loginTitle: 'Prijava',
        loginSubtitle: '',
        email: 'E-mail',
        password: 'Lozinka',
        login: 'Prijava',
        forgotPassword: 'Zaboravljena lozinka?',
        loginFailed: 'Neispravan e-mail ili lozinka.',
        logout: 'Odjava',

        forgotTitle: 'Zaboravljena lozinka',
        forgotSubtitle: 'Unesite svoj e-mail i poslat ćemo vam poveznicu za postavljanje nove lozinke.',
        sendResetLink: 'Pošalji poveznicu',
        forgotDone: 'Ako račun postoji, poveznica za postavljanje lozinke poslana je na vaš e-mail.',
        backToLogin: 'Natrag na prijavu',

        setPasswordTitle: 'Postavljanje lozinke',
        newPassword: 'Nova lozinka',
        repeatPassword: 'Ponovite lozinku',
        savePassword: 'Spremi lozinku',
        passwordTooShort: 'Lozinka mora imati najmanje 5 znakova.',
        passwordsDontMatch: 'Lozinke se ne podudaraju.',
        passwordSaved: 'Lozinka je spremljena. Sada se možete prijaviti.',
        invalidToken: 'Poveznica je nevažeća ili je istekla.',
        checkingLink: 'Provjera poveznice...',

        changePassword: 'Promjena lozinke',
        changePasswordInfo: 'Poslat ćemo vam poveznicu za promjenu lozinke na vaš e-mail.',
        sendChangeLink: 'Pošalji poveznicu za promjenu',
        changeLinkSent: 'Poveznica za promjenu lozinke poslana je na vaš e-mail.',
        confirmPasswordResetTitle: 'Promjena lozinke',
        confirmPasswordResetMessage: 'Poslat ćemo vam poveznicu za promjenu lozinke na e-mail. Želite li nastaviti?',

        // Generic dialog
        confirm: 'Potvrdi',
        cancel: 'Odustani',

        // Language
        croatian: 'Hrvatski',
        english: 'Engleski',

        // Clients
        selectClient: 'Odaberite lokaciju',
        selectClientSubtitle: 'Prijavljeni ste u više klubova. Odaberite u koji želite ući.',
        switchClient: 'Promijeni lokaciju',
        noClients: 'Trenutno nemate pristup nijednom klubu. Obratite se svom klubu.',

        // Navigation
        home: 'Početna',
        menu: 'Izbornik',

        // Calendar
        calendar: 'Kalendar',
        mySessions: 'Moji treninzi',
        availableSessions: 'Dostupni treninzi',
        back: 'Natrag',
        upcoming: 'Nadolazeći',
        past: 'Prošli',
        noMySessions: 'Niste prijavljeni ni na jedan trening.',
        profile: 'Profil',

        // Sports center
        sportsCenter: 'Sportski centar',
        peopleCurrentlyInside: 'Trenutno u sportskom centru',
        sportsCenterHours: 'Radno vrijeme sportskog centra',
        receptionHours: 'Radno vrijeme recepcije',
        weekdaysLong: { 1: 'Ponedjeljak', 2: 'Utorak', 3: 'Srijeda', 4: 'Četvrtak', 5: 'Petak', 6: 'Subota', 7: 'Nedjelja', 8: 'Praznik' },

        // Statistics
        statistics: 'Statistika',
        totalVisits: 'Ukupno posjeta',
        visitsThisMonth: 'Ovaj mjesec',
        visitsThisYear: 'Ova godina',
        productsUsed: 'Iskorišteni proizvodi',
        myVisits: 'Moji posjeti',
        noVisits: 'Još nemate zabilježenih posjeta.',
        loadMore: 'Učitaj još',
        day: 'Dan',
        week: 'Tjedan',
        month: 'Mjesec',
        today: 'Danas',
        noSessions: 'Nema treninga.',
        noSessionsThisDay: 'Za ovaj dan nema treninga.',
        freeSpots: 'slobodno',
        full: 'Popunjeno',
        unlimited: 'Neograničeno',
        signedUp: 'Prijavljeni ste',
        signUp: 'Prijavi se',
        cancelSignup: 'Otkaži prijavu',
        membershipRequired: 'Potrebna članarina',
        sessionPassed: 'Trening je prošao',
        trainer: 'Trener',
        room: 'Dvorana',
        spots: 'Mjesta',
        signedUpCount: 'Prijavljeni',
        joinWaitingList: 'Uđi na listu čekanja',
        leaveWaitingList: 'Napusti listu čekanja',
        onWaitingList: 'Na listi ste čekanja',
        waitingListPosition: 'Vi ste #%n% na listi čekanja',
        registrationClosed: 'Prijave su zatvorene',

        // Signup results
        signupSuccess: 'Uspješno ste se prijavili na trening.',
        cancelSuccess: 'Prijava je otkazana.',
        joinWaitlistSuccess: 'Dodani ste na listu čekanja.',
        leaveWaitlistSuccess: 'Uklonjeni ste s liste čekanja.',
        errorMembershipRequired: 'Za ovaj trening potrebna je važeća članarina.',
        errorCapacityFull: 'Trening je popunjen.',
        errorSessionPassed: 'Ovaj trening je već prošao.',
        errorRegistrationClosed: 'Prijave za ovaj trening su zatvorene.',
        genericError: 'Došlo je do pogreške. Pokušajte ponovno.',

        // Profile
        activeMembership: 'Aktivna članarina',
        membershipUntil: 'vrijedi do',
        noActiveMembership: 'Nema aktivne članarine',
        account: 'Račun',
        changePhoto: 'Promijeni fotografiju',
        removePhoto: 'Ukloni fotografiju',
        photoUpdated: 'Fotografija je ažurirana.',
        photoRemoved: 'Fotografija je uklonjena.',
        invalidImage: 'Odaberite ispravnu sliku (JPG, PNG, GIF ili WEBP).',
        confirmRemovePhotoTitle: 'Ukloni fotografiju',
        confirmRemovePhotoMessage: 'Želite li ukloniti svoju profilnu fotografiju?',

        weekdaysShort: ['Ned', 'Pon', 'Uto', 'Sri', 'Čet', 'Pet', 'Sub'],
        monthsGenitive: ['siječnja', 'veljače', 'ožujka', 'travnja', 'svibnja', 'lipnja', 'srpnja', 'kolovoza', 'rujna', 'listopada', 'studenoga', 'prosinca']
    },
    en: {
        appName: 'Fytlix',

        // Login / auth
        loginTitle: 'Sign in',
        loginSubtitle: '',
        email: 'E-mail',
        password: 'Password',
        login: 'Sign in',
        forgotPassword: 'Forgot password?',
        loginFailed: 'Incorrect e-mail or password.',
        logout: 'Log out',

        forgotTitle: 'Forgot password',
        forgotSubtitle: 'Enter your e-mail and we will send you a link to set a new password.',
        sendResetLink: 'Send link',
        forgotDone: 'If an account exists, a link to set your password has been sent to your e-mail.',
        backToLogin: 'Back to sign in',

        setPasswordTitle: 'Set password',
        newPassword: 'New password',
        repeatPassword: 'Repeat password',
        savePassword: 'Save password',
        passwordTooShort: 'Password must be at least 5 characters.',
        passwordsDontMatch: 'Passwords do not match.',
        passwordSaved: 'Password saved. You can now sign in.',
        invalidToken: 'The link is invalid or has expired.',
        checkingLink: 'Checking link...',

        changePassword: 'Change password',
        changePasswordInfo: 'We will send a link to change your password to your e-mail.',
        sendChangeLink: 'Send change link',
        changeLinkSent: 'A link to change your password has been sent to your e-mail.',
        confirmPasswordResetTitle: 'Change password',
        confirmPasswordResetMessage: 'We will send a link to change your password to your e-mail. Do you want to continue?',

        // Generic dialog
        confirm: 'Confirm',
        cancel: 'Cancel',

        // Language
        croatian: 'Croatian',
        english: 'English',

        // Clients
        selectClient: 'Choose a location',
        selectClientSubtitle: 'You belong to more than one club. Choose which one to enter.',
        switchClient: 'Switch location',
        noClients: 'You currently have no access to any club. Please contact your club.',

        // Navigation
        home: 'Home',
        menu: 'Menu',

        // Calendar
        calendar: 'Calendar',
        mySessions: 'My sessions',
        availableSessions: 'Available sessions',
        back: 'Back',
        upcoming: 'Upcoming',
        past: 'Past',
        noMySessions: 'You are not signed up for any sessions.',
        profile: 'Profile',

        // Sports center
        sportsCenter: 'Sports center',
        peopleCurrentlyInside: 'Currently in the sports center',
        sportsCenterHours: 'Sports center working hours',
        receptionHours: 'Reception working hours',
        weekdaysLong: { 1: 'Monday', 2: 'Tuesday', 3: 'Wednesday', 4: 'Thursday', 5: 'Friday', 6: 'Saturday', 7: 'Sunday', 8: 'Holiday' },

        // Statistics
        statistics: 'Statistics',
        totalVisits: 'Total visits',
        visitsThisMonth: 'This month',
        visitsThisYear: 'This year',
        productsUsed: 'Products used',
        myVisits: 'My visits',
        noVisits: 'You have no recorded visits yet.',
        loadMore: 'Load more',
        day: 'Day',
        week: 'Week',
        month: 'Month',
        today: 'Today',
        noSessions: 'No sessions.',
        noSessionsThisDay: 'No sessions for this day.',
        freeSpots: 'free',
        full: 'Full',
        unlimited: 'Unlimited',
        signedUp: 'You are signed up',
        signUp: 'Sign up',
        cancelSignup: 'Cancel signup',
        membershipRequired: 'Membership required',
        sessionPassed: 'Session has passed',
        trainer: 'Trainer',
        room: 'Room',
        spots: 'Spots',
        signedUpCount: 'Signed up',
        joinWaitingList: 'Join waiting list',
        leaveWaitingList: 'Leave waiting list',
        onWaitingList: 'You are on the waiting list',
        waitingListPosition: 'You are #%n% on the waiting list',
        registrationClosed: 'Sign-ups are closed',

        // Signup results
        signupSuccess: 'You have successfully signed up for the session.',
        cancelSuccess: 'Your signup has been cancelled.',
        joinWaitlistSuccess: 'You have been added to the waiting list.',
        leaveWaitlistSuccess: 'You have been removed from the waiting list.',
        errorMembershipRequired: 'This session requires a valid membership.',
        errorCapacityFull: 'The session is full.',
        errorSessionPassed: 'This session has already passed.',
        errorRegistrationClosed: 'Sign-ups for this session are closed.',
        genericError: 'Something went wrong. Please try again.',

        // Profile
        activeMembership: 'Active membership',
        membershipUntil: 'valid until',
        noActiveMembership: 'No active membership',
        account: 'Account',
        changePhoto: 'Change photo',
        removePhoto: 'Remove photo',
        photoUpdated: 'Photo updated.',
        photoRemoved: 'Photo removed.',
        invalidImage: 'Please choose a valid image (JPG, PNG, GIF or WEBP).',
        confirmRemovePhotoTitle: 'Remove photo',
        confirmRemovePhotoMessage: 'Do you want to remove your profile photo?',

        weekdaysShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
        monthsGenitive: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
    }
};

function table() {
    return strings[store.state.language] || strings.hr;
}

// Change the active language everywhere: the store (which drives $t / $strings reactivity and persists to
// localStorage) and moment's locale (which localises all date formatting).
export function applyLanguage(language) {
    if (!strings[language]) language = 'hr';
    store.commit('setLanguage', language);
    moment.locale(language);
}

export default {
    install(app) {
        const t = (key, params) => {
            let value = table()[key];
            if (value === undefined) value = strings.hr[key];
            if (value === undefined) return key;
            if (params && typeof value === 'string') {
                for (const p in params) {
                    value = value.split('%' + p + '%').join(params[p]);
                }
            }
            return value;
        };
        app.config.globalProperties.$t = t;
        Object.defineProperty(app.config.globalProperties, '$strings', {
            get() {
                return table();
            }
        });
    }
};
